/**
 * The messages panel piece (web-account-pages D2): the client's messages with the studio in one
 * stream over any page, sliding in from the right under the site header. The studio's notices,
 * its replies and the client's own messages from both rooms in time order; each message marked
 * with its room; filters; Send as a secure message.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'messages-panel',
  description:
    "The client's messages with the studio in one stream over any page: notices, replies and the client's own messages from the Secure room and the Telegram chat in time order, each marked with its room, with filters and a composer that sends a secure message.",
  options: {
    showFilters: { type: 'boolean', default: true, description: 'Show the filter row (All, Secure, Telegram, Chat, Bookings, Forms, Payments).', showsPart: 'filters' },
  },
  parts: {
    scrim: { description: 'The dimmed page behind the open panel, under the site header; a click closes the panel.', required: false },
    panel: { description: 'The panel itself.', required: false },
    header: { description: "The panel's header: the studio's mark, its name and the close button.", required: false },
    logo: { description: "The studio's logo, when it has one.", required: false },
    name: { description: "The studio's name.", required: false, role: 'title' },
    rooms: { description: 'Secure · Telegram under the name, each with its mark.', required: false },
    close: { description: 'Closes the panel.', required: false },
    filters: { description: 'The filter row.', required: false },
    filter: { description: 'One filter.', required: false, states: ['selected'] },
    thread: { description: 'The stream, newest at the bottom; scrolling to the top loads older messages.', required: false },
    older: { description: 'Shown while older messages load.', required: false },
    day: { description: 'A day divider: Today, Yesterday, or the date.', required: false },
    message: { description: 'A message: the client\'s own on the right, the studio\'s on the left.', required: false },
    notice: { description: "A notice the studio's bot sent through Telegram, as the client received it.", required: false },
    media: { description: 'Media the client sent through Telegram, as a quiet line.', required: false },
    roomMark: { description: 'The lock on a secure message or the Telegram mark on any other, beside its time.', required: false },
    door: { description: "A notice's action: the matching page on this website.", required: false },
    stateLine: { description: "A form request's or an invite's state under its notice.", required: false },
    preparation: { description: "A reminder's preparation pages, folded under it.", required: false },
    sendIssue: { description: 'Under a message that did not send: Not sent, with a retry when one can work.', required: false },
    empty: { description: 'What shows when the chosen filter has nothing yet.', required: false },
    error: { description: 'What shows when the messages could not be read, with Try again.', required: false },
    unavailable: { description: 'What shows when the client has no chat with the studio.', required: false },
    loading: { description: 'Shown while the messages are read.', required: false },
    typing: { description: 'The studio is typing.', required: false },
    composer: { description: 'The message field and Send.', required: false },
    send: { description: 'Sends the message as a secure message.', required: false },
  },
  actions: {
    close: { description: 'Closes the panel; the page stays where it was.', args: [] },
    chooseFilter: { description: 'Shows one filter of the stream.', args: [{ name: 'id', kind: 'item', list: 'filters', key: 'id' }] },
    loadOlder: { description: 'Loads older messages.', args: [] },
    send: { description: 'Sends the words as a secure message to the studio.', args: [{ name: 'text', kind: 'value', type: 'string', format: 'text' }] },
    retrySend: { description: 'Sends a message that did not send again, under the same key.', args: [{ name: 'id', kind: 'item', list: 'days[].messages', key: 'id' }] },
    typing: { description: 'Tells the studio the client is typing.', args: [] },
    openDoor: { description: "Opens a notice's page on this website and closes the panel.", args: [{ name: 'href', kind: 'value', type: 'string', format: 'path' }] },
    retry: { description: 'Reads the messages again after they could not be read.', args: [] },
  },
  events: {
    closed: 'The panel closed.',
    sent: 'The client sent a message.',
    doorOpened: "The client opened a notice's page.",
  },
});

/** The filters, in the row's order. */
export type MessagesFilterId = 'all' | 'secure' | 'telegram' | 'chat' | 'bookings' | 'forms' | 'payments';

export interface MessagesFilter {
  readonly id: MessagesFilterId;
  /** All, Secure, Telegram, Chat, Bookings, Forms or Payments. */
  readonly label: string;
  readonly current: boolean;
}

/** One entry of the stream, ready to draw. */
export interface PanelMessage {
  /** The message's id (the composer's key until a sent message is stored). */
  readonly id: string;
  /** Which room it is in: `secure` draws the lock, `telegram` the Telegram mark. */
  readonly room: 'secure' | 'telegram';
  /** Who sent it. */
  readonly from: 'you' | 'studio';
  /** A message, a notice from the studio's bot, or the line for media the client sent. */
  readonly kind: 'message' | 'notice' | 'media';
  /** The words: the message, the media line, or a notice's label when it has no stored copy. */
  readonly text: string;
  /** A notice's words as display-safe markup; null when it has none (draw `text`). */
  readonly html: string | null;
  /** The time it was sent, in the client's own time zone. */
  readonly time: string;
  /** The client's own secure message: sending until stored; unsent or refused when it did not send; else null. */
  readonly delivery: 'sending' | 'stored' | 'unsent' | 'refused' | null;
  /** A notice's action on this website; null when there is none. */
  readonly door: { readonly label: string; readonly href: string } | null;
  /** A form request's or an invite's state: Waiting for you, Submitted Oct 12, Expired, Withdrawn, Booked, Passed. */
  readonly state: string | null;
  /** That state's own status, for its icon (the Mini App's state line): a tick when submitted or booked, a clock while it waits, else a slash. */
  readonly stateStatus: 'awaiting' | 'submitted' | 'expired' | 'withdrawn' | 'booked' | 'pending' | 'passed' | null;
  /** A reminder's preparation pages, as display-safe markup. */
  readonly preparation: readonly { readonly id: string; readonly html: string | null }[];
  /** The fold's heading over those pages: the studio's name for its notes, How to prepare unless it named its own; null without pages. */
  readonly preparationLabel: string | null;
}

export interface MessagesDay {
  readonly key: string;
  /** Today, Yesterday, or the date. */
  readonly label: string;
  readonly messages: readonly PanelMessage[];
}

/** What the messages panel knows. */
export interface MessagesPanelState {
  /** True while the panel is open. */
  readonly open: boolean;
  /** The studio's name. */
  readonly studioName: string;
  /** The studio's logo image, or null. */
  readonly logoUrl: string | null;
  readonly filters: readonly MessagesFilter[];
  /** loading while the chosen filter is read, error when it could not be. */
  readonly status: 'loading' | 'ready' | 'not-found' | 'error';
  /** The stream by day, oldest first. */
  readonly days: readonly MessagesDay[];
  /** True while older messages remain. */
  readonly hasOlder: boolean;
  readonly loadingOlder: boolean;
  /** The studio is typing. */
  readonly typing: boolean;
  /** The message the panel was opened at (a banner's), to bring into view; null for the newest. */
  readonly focusId: string | null;
  /** The composer's draft is kept under this name for the browser session. */
  readonly draftScope: string;
}

/** What a drawing can ask the messages panel to do. */
export interface MessagesPanelActions {
  readonly close: () => void;
  readonly chooseFilter: (id: MessagesFilterId) => void;
  readonly loadOlder: () => void;
  readonly send: (text: string) => void;
  readonly retrySend: (id: string) => void;
  readonly typing: () => void;
  readonly openDoor: (href: string) => void;
  readonly retry: () => void;
}

/** What the messages panel tells its host. */
export interface MessagesPanelEvents {
  readonly closed: Record<string, never>;
  readonly sent: Record<string, never>;
  readonly doorOpened: { readonly href: string };
}
